import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { osName } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { DEBUG_FLAG } from "../../constants/debug";
import { LOCAL_STORAGE_KEY } from "../../constants/localStorage";
import { VERSION } from "../../constants/version";
import { useAuth } from "../../context/auth-context";
import { useMessage } from "../../context/message-context";
import apiRequest from "../../functions/api";
import { useLocalStorage, useSessionStorage } from "../../utils/hooks";
import { FileUploadComponent } from "../lib";

const program = "SUPAPI";

const SupportDialog = ({ supportDialog, setSupportDialog }) => {
  const { t } = useTranslation();
  const [accessToken] = useSessionStorage(LOCAL_STORAGE_KEY.accessToken);
  const { user } = useAuth();
  const { showSuccessMessage, showErrorMessage } = useMessage();
  const releaseVersion = localStorage.getItem(LOCAL_STORAGE_KEY.version);
  const [deviceId] = useLocalStorage(DEBUG_FLAG() ? LOCAL_STORAGE_KEY.deviceIdLocal : LOCAL_STORAGE_KEY.deviceId);
  const [username] = useLocalStorage(LOCAL_STORAGE_KEY.username);
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [kategorien, setKategorien] = useState([]);
  const [files, setFiles] = useState([]);
  const maxFileSize = 20 * 1024 * 1024; //20 MB
  const displayFiles = [];
  const [supportForm, setSupportForm] = useState({
    supportKat: "",
    email: "",
    beschreibung: "",
  });

  const supportDaten = {
    Version: releaseVersion,
    Build: VERSION,
    DeviceId: deviceId,
    Betriebssystem: osName,
    Username: username,
    Aufruf: "Menu",
    Pfad: location.pathname,
  };

  useEffect(() => {
    if (!supportDialog) return;

    const payload = {
      statement: "INIT",
    };

    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    apiRequest("POST", "support-anfrage", payload, headers, initCb);
  }, [supportDialog]);

  const initCb = (response) => {
    setIsLoading(false);
    if (response.status === 200) {
      setKategorien(response.data?.kategorie);
    } else {
      if (response) {
        showErrorMessage(response.data.message);
      } else {
        showErrorMessage(t("Keine Internetverbindung"));
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prüfen ob Dateien > 20MB sind weil dies das Outlook maximum wäre (wenn das ein Problem ist dann wtf?)
    const totalSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);
    if (totalSize > maxFileSize) {
      showErrorMessage(t("Die angehängten Dateien sind zu groß. Bitte bleiben Sie unter gesamt 20 MB."));
      return;
    }

    setSupportDialog(false);
    // Dateien für versand Vorbereiten
    const fileNames = Array.from(files).map((file) => file.name);
    const filePromises = Array.from(files).map(
      (file) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result); // Base64-Daten
          reader.readAsDataURL(file); // Datei als Base64 einlesen
        })
    );

    // Warten auf alle Dateien
    const base64Files = await Promise.all(filePromises);

    setIsLoading(true);
    const payload = {
      program: program,
      statement: "ANFRAGE_SENDEN",
      formData: supportForm,
      kategorien: kategorien,
      supportDaten: supportDaten,
      dateiNamen: fileNames,
      dateiDaten: base64Files,
    };

    const headers = {
      Authorization: "Bearer " + accessToken,
      statement: "ANFRAGE_SENDEN",
      program: program,
    };
    apiRequest("POST", "support-anfrage", payload, headers, speichernCb);
  };

  const speichernCb = (response) => {
    setIsLoading(false);
    if (response.status === 200) {
      setFiles([]);
      showSuccessMessage("Support erfolgreich benachrichtigt!");
      setSupportForm({
        supportKat: "",
        email: "",
        beschreibung: "",
      });
    } else {
      setSupportDialog(true);
      if (response) {
        showErrorMessage(response.data.message);
      } else {
        showErrorMessage(t("Keine Internetverbindung"));
      }
    }
  };

  const handleChange = (event, newValue, newName) => {
    const { name, value } = event.target;
    if (name) {
      setSupportForm({ ...supportForm, [name]: value });
    } else if (newValue?.key) {
      setSupportForm({ ...supportForm, [newName]: newValue?.key });
    }
  };

  const removeFile = (index) => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
  };

  const appendFile = (appendFiles) => {
    const updatedFiles = [...files, ...appendFiles];
    setFiles(updatedFiles);
  };

  const mails = [
    { label: user.mail, value: user.mail },
    { label: user.mail2, value: user.mail2 },
  ];

  for (let i = 0; i < files.length; i++) {
    displayFiles.push(
      <Box
        sx={{
          display: "flex", // Flexbox aktivieren
          alignItems: "center", // Vertikale Ausrichtung der Elemente
          justifyContent: "space-between", // Abstand zwischen den Elementen
          marginBottom: "8px", // Abstand zwischen den Listenelementen
        }}
      >
        <li key={i}>{files[i].name}</li>
        <IconButton onClick={() => removeFile(i)}>
          <CloseIcon color="error" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Dialog open={supportDialog} maxWidth="md" onClose={() => setSupportDialog(false)} fullWidth>
      <DialogContent sx={{ height: "96" }}>
        <DialogContentText style={{ fontSize: 22, fontWeight: "bold" }}>Supportanfrage erstellen</DialogContentText>
        <form onSubmit={handleSubmit} id="aufgaben-form">
          <Grid container justifyContent="center">
            <Grid item xs={12} sm={6}>
              <Grid item sm={12}>
                <Box p={1}>
                  <TextField
                    select
                    id="supportKat"
                    name="supportKat"
                    label={t("In welchen Bereich fällt ihre Anfrage?")}
                    value={supportForm.supportKat}
                    onChange={handleChange}
                    fullWidth
                    required
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  >
                    {kategorien?.map((kategori) => {
                      return (
                        <MenuItem key={kategori.key} value={kategori.key}>
                          {t(kategori.name)}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Box>
              </Grid>
              <Grid item sm={12}>
                <Box p={1}>
                  <Autocomplete
                    multiple={false}
                    fullWidth={true}
                    onChange={(event, newValue) => {
                      const getKeys = () => {
                        if (typeof newValue === "string") {
                          return newValue;
                        } else {
                          return newValue?.value;
                        }
                      };
                      const result = getKeys();
                      setSupportForm({ ...supportForm, email: result });
                    }}
                    value={supportForm?.email}
                    filterOptions={(options, params) => {
                      console.log(options);
                      if (options[0] === undefined) {
                        if (params.inputValue !== "") {
                          return [
                            {
                              value: params.inputValue,
                              label: `"${params.inputValue}" verwenden`,
                            },
                          ];
                        } else {
                          return [];
                        }
                      }
                      const optionen = options?.filter((e) => e.label !== "" && e.label.toUpperCase().includes(params.inputValue.toUpperCase()));
                      if (params.inputValue !== "") {
                        optionen.push({
                          value: params.inputValue,
                          label: `"${params.inputValue}" verwenden`,
                        });
                      }
                      return optionen;
                    }}
                    id="inhalt"
                    options={mails}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") {
                        return option;
                      }
                      if (option.value) {
                        return option.value;
                      }
                      return option.label;
                    }}
                    renderOption={(props, option) => {
                      const { key, ...optionProps } = props;
                      return (
                        <li key={key} {...optionProps}>
                          {option.label}
                        </li>
                      );
                    }}
                    noOptionsText="Keine hinterlegten Mails gefunden"
                    renderInput={(params) => <TextField {...params} required label={t("Bitte geben Sie Ihre E-Mail Adresse ein")} />}
                  />
                  <Typography align="center" variant="caption">
                    {t("Sie können frei eine Ihrer E-Mail Adressen eintippen oder auswählen.")}
                  </Typography>
                </Box>
              </Grid>
              <Grid item sm={12}>
                <Box p={1}>
                  <TextField
                    variant="outlined"
                    label={t("Betreff")}
                    value={supportForm.betreff}
                    onChange={handleChange}
                    id="Betreff"
                    name="betreff"
                    fullWidth
                    required
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Box>
              </Grid>

              <Grid item sm={12}>
                <Box p={1}>
                  <TextField
                    variant="outlined"
                    multiline
                    rows={5}
                    label={t("Bitte beschreiben sie das Problem")}
                    value={supportForm.beschreibung}
                    onChange={handleChange}
                    id="beschreibung"
                    name="beschreibung"
                    fullWidth
                    required
                    margin="normal"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Box>
              </Grid>

              <Grid item sm={12}>
                <Box p={1}>
                  <FileUploadComponent
                    multiple
                    variant="contained"
                    children="Dokumente anhängen"
                    color="primary"
                    id="file-input"
                    name="file-input"
                    onChange={appendFile}
                  />
                </Box>

                {displayFiles}
                {/* {files?.map((file) => (
                  <li >{file.name}</li>
                ))} */}
              </Grid>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button style={{ marginRight: "10px" }} display="flex" onClick={() => setSupportDialog(false)}>
          {t("Abbrechen")}
        </Button>
        <Button color="primary" variant="contained" type="submit" form="aufgaben-form">
          {t("Absenden")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupportDialog;
